import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { APIError, api, jsonBody } from "../../api/client";
import type { EventConflict, EventRecord } from "../../api/types";
import { Card, EditorPage, ErrorPanel } from "../../ui/components";
import { type ConflictResult, EventConflictPanel } from "./EventConflictPanel";
import { EventForm, type EventPayload } from "./EventForm";

export function EventCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<unknown>();
  const [submitting, setSubmitting] = useState(false);
  const [conflicts, setConflicts] = useState<ConflictResult | null>(null);
  const [pending, setPending] = useState<EventPayload | null>(null);
  // Arriving from a square on the calendar means that day was the point. Nine in the morning is
  // what /calendar/new makes of the same parameter, so both sides of the diary answer a click on
  // the same square the same way.
  const requestedDate = searchParams.get("date");
  const requested = requestedDate ? new Date(`${requestedDate}T09:00:00`) : undefined;
  const defaultStart = requested && !Number.isNaN(requested.getTime()) ? requested : undefined;

  async function create(payload: EventPayload, overrideReason: string | null) {
    setSubmitting(true);
    setError(undefined);
    try {
      const created = await api<EventRecord>("/api/v1/events", {
        method: "POST",
        body: jsonBody({
          ...payload,
          confirmConflictOverride: overrideReason !== null,
          conflictOverrideReason: overrideReason ?? "",
        }),
      });
      await queryClient.invalidateQueries({ queryKey: ["events"] });
      await queryClient.invalidateQueries({ queryKey: ["calendar"] });
      navigate(`/events/${created.id}`, { replace: true });
    } catch (caught) {
      // A clash is not a failure, it is a decision to put in front of somebody. Everything else is
      // an error they cannot act on from here.
      if (caught instanceof APIError && caught.code === "event_conflict") {
        setConflicts({
          conflicts: (caught.details.conflicts as EventConflict[]) ?? [],
          canOverride: Boolean(caught.details.canOverride),
        });
        setPending(payload);
      } else {
        setError(caught);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function check(payload: EventPayload) {
    setSubmitting(true);
    setError(undefined);
    try {
      const result = await api<ConflictResult>("/api/v1/events/check-conflicts", {
        method: "POST",
        body: jsonBody({
          ...payload,
          confirmConflictOverride: false,
          conflictOverrideReason: "",
        }),
      });
      if (result.conflicts.length) {
        setConflicts(result);
        setPending(payload);
        return;
      }
      await create(payload, null);
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <EditorPage
      title="Plan an event"
      description="An event the organization is running. It can hold rooms of its own, or sit on a booking somebody already made."
      backTo="/events"
      backLabel="Back to events"
    >
      {error ? <ErrorPanel error={error} /> : null}
      {conflicts ? (
        <EventConflictPanel
          result={conflicts}
          submitting={submitting}
          onCancel={() => {
            setConflicts(null);
            setPending(null);
          }}
          onOverride={(reason) => {
            if (pending) void create(pending, reason);
          }}
        />
      ) : (
        <Card>
          <EventForm
            mode="create"
            defaultStart={defaultStart}
            submitting={submitting}
            submitLabel="Check and create"
            onCancel={() => navigate("/events")}
            onSubmit={(payload) => void check(payload)}
          />
        </Card>
      )}
    </EditorPage>
  );
}
